/**
 * Error handling utilities for the Member Web Portal
 * Provides consistent error handling, logging, and user-friendly messages
 */

export class AppError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public isOperational = true
  ) {
    super(message);
    this.name = 'AppError';
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

export class NetworkError extends AppError {
  constructor(message = 'Network error occurred. Please check your connection.') {
    super(message, 'NETWORK_ERROR', 0);
    this.name = 'NetworkError';
  }
}

export class AuthenticationError extends AppError {
  constructor(message = 'Authentication failed. Please sign in again.') {
    super(message, 'AUTH_ERROR', 401);
    this.name = 'AuthenticationError';
  }
}

export class PermissionError extends AppError {
  constructor(message = 'You do not have permission to perform this action.') {
    super(message, 'PERMISSION_ERROR', 403);
    this.name = 'PermissionError';
  }
}

export class ValidationError extends AppError {
  constructor(message = 'Invalid input. Please check your data.', public fields?: Record<string, string>) {
    super(message, 'VALIDATION_ERROR', 400);
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends AppError {
  constructor(message = 'The requested resource was not found.') {
    super(message, 'NOT_FOUND', 404);
    this.name = 'NotFoundError';
  }
}

/**
 * Get user-friendly error message from error object
 */
export function getUserFriendlyMessage(error: unknown): string {
  if (error instanceof AppError) {
    return error.message;
  }

  if (error instanceof Error) {
    // Cognito errors (check before Firebase since Cognito errors also have a .code property)
    if (error.name === 'NotAuthorizedException') {
      return 'Invalid email or password.';
    }
    if (error.name === 'UserNotFoundException') {
      return 'User not found.';
    }
    if (error.name === 'UsernameExistsException') {
      return 'An account with this email already exists.';
    }
    if (error.name === 'InvalidPasswordException') {
      return 'Password does not meet requirements.';
    }
    if (error.name === 'CodeMismatchException') {
      return 'Invalid verification code.';
    }
    if (error.name === 'ExpiredCodeException') {
      return 'Verification code has expired.';
    }

    // Firebase errors
    if ('code' in error) {
      const firebaseCode = (error as any).code;
      const firebaseMessage = getFirebaseErrorMessage(firebaseCode);
      if (firebaseMessage !== 'An error occurred. Please try again.') {
        return firebaseMessage;
      }
    }

    return error.message || 'An unexpected error occurred.';
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Get user-friendly message for Firebase error codes
 */
function getFirebaseErrorMessage(code: string): string {
  const errorMessages: Record<string, string> = {
    'auth/invalid-email': 'Invalid email address.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'User not found.',
    'auth/wrong-password': 'Invalid password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password is too weak.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'permission-denied': 'You do not have permission to access this resource.',
    'not-found': 'The requested resource was not found.',
    'already-exists': 'This resource already exists.',
    'resource-exhausted': 'Resource limit exceeded. Please try again later.',
    'unauthenticated': 'Please sign in to continue.',
    'unavailable': 'Service temporarily unavailable. Please try again.',
    'deadline-exceeded': 'Request timed out. Please try again.',
  };

  return errorMessages[code] || 'An error occurred. Please try again.';
}

/**
 * Log error for debugging and monitoring
 */
export function logError(error: unknown, context?: string): void {
  const timestamp = new Date().toISOString();
  const errorInfo = {
    timestamp,
    context,
    error: error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
    } : error,
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', errorInfo);
  }

  // In production, send to error tracking service
  if (process.env.NODE_ENV === 'production') {
    // TODO: Send to error tracking service (e.g., Sentry)
    // Sentry.captureException(error, { extra: { context } });
  }
}

/**
 * Retry function with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelay = 1000,
  maxDelay = 10000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (
        error instanceof AuthenticationError ||
        error instanceof PermissionError ||
        error instanceof ValidationError
      ) {
        throw error;
      }

      // Don't retry if this was the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);
      
      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.3 * delay;
      const totalDelay = delay + jitter;

      logError(error, `Retry attempt ${attempt + 1}/${maxRetries} after ${totalDelay}ms`);
      
      await new Promise(resolve => setTimeout(resolve, totalDelay));
    }
  }

  throw lastError;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof NetworkError) {
    return true;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      message.includes('timeout') ||
      (error as any).code === 'auth/network-request-failed'
    );
  }

  return false;
}

/**
 * Check if error is transient (can be retried)
 */
export function isTransientError(error: unknown): boolean {
  if (isNetworkError(error)) {
    return true;
  }

  if (error instanceof Error) {
    const code = (error as any).code;
    return (
      code === 'unavailable' ||
      code === 'deadline-exceeded' ||
      code === 'resource-exhausted'
    );
  }

  return false;
}

/**
 * Handle async errors with consistent error handling
 */
export async function handleAsync<T>(
  promise: Promise<T>,
  context?: string
): Promise<[T | null, AppError | null]> {
  try {
    const data = await promise;
    return [data, null];
  } catch (error) {
    logError(error, context);
    
    if (error instanceof AppError) {
      return [null, error];
    }

    // Convert to AppError
    const appError = new AppError(
      getUserFriendlyMessage(error),
      'UNKNOWN_ERROR',
      500
    );
    
    return [null, appError];
  }
}
